import { Group, Loader, Select, SelectProps, Stack, Text } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { OnlineStatus } from '@/components/devices/OnlineStatus/OnlineStatus'
import { VlmStatusBadge } from '@/components/vlm/VlmStatusBadge'
import { useGetDeviceList } from '@/queries/deviceQueries'
import { getVlmStatus, isJetsonDevice } from '@/utils/device/getDeviceAttr'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'

type DeviceSelectProps = Omit<SelectProps, 'data' | 'renderOption'>

export const DeviceSelect = ({
  value,
  onChange,
  error,
  ...rest
}: DeviceSelectProps) => {
  const intl = useIntl()
  const { data, isLoading } = useGetDeviceList({
    limit: MAX_ITEMS_WITHOUT_PAGINATION,
    ordering: 'label'
  })

  const devices = useMemo(() => data?.results ?? [], [data])

  // Preselect the first online device if none is selected
  useEffect(() => {
    if (!value && devices.length > 0) {
      const firstOnline = devices.find((d) => d.active && !isJetsonDevice(d))

      if (firstOnline) {
        onChange?.(firstOnline.id, {
          value: firstOnline.id,
          label: firstOnline.label
        })
      }
    }
  }, [devices, value, onChange])

  const deviceMetaMap = useMemo(
    () =>
      Object.fromEntries(
        devices.map((d) => [
          d.id,
          {
            active: d.active,
            isJetson: !!isJetsonDevice(d),
            vlmStatus: getVlmStatus(d)?.service_status
          }
        ])
      ),
    [devices]
  )

  const selectedMeta = value ? deviceMetaMap[value] : undefined

  const selectData = devices.map((d) => ({
    value: d.id,
    label: d.label,
    disabled: !d.active
  }))

  const deviceWarning = useMemo(() => {
    if (!selectedMeta) {
      return undefined
    }

    if (selectedMeta.isJetson) {
      return intl.formatMessage({
        id: 'vlm.pipelines.create.device.jetson.unsupported'
      })
    }

    if (!selectedMeta.vlmStatus) {
      return intl.formatMessage({
        id: 'vlm.pipelines.create.device.vlmNotInstalled'
      })
    }

    return undefined
  }, [selectedMeta, intl])

  return (
    <Stack gap="xs">
      <Select
        data={selectData}
        allowDeselect={false}
        maxDropdownHeight={400}
        rightSection={isLoading ? <Loader size="xs" /> : undefined}
        renderOption={({ option, checked }) => (
          <Group flex="1" gap="xs" align="center">
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                {option.label}
              </Text>
              <OnlineStatus
                status={
                  deviceMetaMap[option.value]?.active ? 'online' : 'offline'
                }
              />
            </Stack>

            {checked && (
              <IconCheck style={{ marginInlineStart: 'auto' }} size={16} />
            )}
          </Group>
        )}
        value={value}
        onChange={onChange}
        {...rest}
        error={deviceWarning ?? error}
      />

      {selectedMeta && !selectedMeta.isJetson && (
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            <FormattedMessage id="vlm.hardware.title" />:
          </Text>
          <VlmStatusBadge status={selectedMeta.vlmStatus} />
        </Group>
      )}
    </Stack>
  )
}
